import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './SearchFilter.module.scss';
import { keysToCamelFromSnake } from '../../utils/caseconverters';
import SearchInput from '../SearchInput';
import SearchChips from '../SearchChips';
import SearchDropdownRegion from '../SearchDropdownRegion';
import SearchResults from '../SearchResults';
import { useContextState } from '../StateProvider';
import { useTranslation } from 'next-i18next';
import * as api from '../../utils/api';
import isEmpty from '../../utils/isEmpty';
import classNames from 'classnames';

const useItems = () => {
    const [
        {
            searching,
            searchInput,
            activeChips,
            activeRegions,
            searchSettings,
            sorting,
            currentPage,
        } = {},
        dispatch,
    ] = useContextState();
    const [items, setItems] = useState([]);
    const shouldFetchOnMount = useRef(false);
    const currentPromise = useRef(null);

    useEffect(() => {
        if (!searching || !shouldFetchOnMount.current) {
            // Set to true so this doesn't get triggered again
            shouldFetchOnMount.current = true;
            return;
        }

        const fetchItems = async () => {
            let ac;

            if ('AbortController' in window) {
                ac = new AbortController();
            }

            if (currentPromise.current) {
                if (ac) {
                    ac.abort();
                }
            }

            const result = api.getSearchResults(
                currentPage || 1,
                searchInput,
                activeChips || '',
                activeRegions || '',
                searchSettings?.parent || '',
                searchSettings?.pinnedCategory || '',
                sorting || '',
                ac
            );
            currentPromise.current = result;

            result
                .then((resp) => {
                    resp.json().then((data) => {
                        data = keysToCamelFromSnake(data);
                        if (data.results) {
                            let results;

                            if (currentPage > 1) {
                                results = [...items, ...data.results];
                            } else {
                                results = data.results;
                            }

                            setItems(results);

                            if (
                                typeof window !== 'undefined' &&
                                results.length === 0
                            ) {
                                window.dataLayer.push({
                                    event: 'searchNoResults',
                                    searchQuery: searchInput,
                                });
                            }

                            if (searchSettings.useEndCategories) {
                                dispatch({
                                    type: 'set_categories',
                                    payload: data.facets.endCategories,
                                });
                            } else {
                                dispatch({
                                    type: 'set_categories',
                                    payload: data.facets.rootCategories,
                                });
                            }
                            dispatch({
                                type: 'set_meta',
                                payload: data.meta,
                            });
                            dispatch({ type: 'set_searching', payload: false });
                        }
                        currentPromise.current = null;
                    });
                })
                .catch((e) => {
                    // When abort() is called, the fetch() promise rejects with an AbortError.
                    if (
                        !(e instanceof DOMException && e.name === 'AbortError')
                    ) {
                        throw e;
                    }
                });
        };

        fetchItems();
    }, [
        activeChips,
        activeRegions,
        dispatch,
        searchInput,
        searchSettings?.parent,
        searching,
        sorting,
        currentPage,
        items,
        searchSettings?.useEndCategories,
        searchSettings?.pinnedCategory,
    ]);

    return items;
};

const SearchFilter = ({ children, untranslatedButtonModifiers = [] }) => {
    const { t } = useTranslation('common');
    const items = useItems();
    const [{ regions, isGlobal } = {}, dispatch] = useContextState();

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (window.location.search) {
            dispatch({ type: 'set_searching', payload: true });
        }
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */

    const classes = classNames(
        styles['SearchFilter'],
        {
            [styles['SearchFilter--Global']]: isGlobal,
            [styles['SearchFilter--NonGlobal']]: !isGlobal,
        },
        untranslatedButtonModifiers.map((x) => styles[x])
    );

    const headline = isGlobal ? 'search.headlineGlobal' : 'search.headline';
    const HeadlineWrap = isGlobal ? 'h1' : 'div';

    return (
        <>
            <div className={classes}>
                <div className={styles['SearchFilter__Wrap']}>
                    <HeadlineWrap>
                        <label
                            htmlFor="search"
                            className={styles['SearchFilter__HeadingLabel']}>
                            {t(headline)}
                        </label>
                    </HeadlineWrap>
                    <SearchInput />
                    <div className={styles['SearchFilter__Actions']}>
                        <SearchChips />
                        {!isEmpty(regions) && <SearchDropdownRegion />}
                    </div>
                </div>
            </div>
            {isEmpty(items) && children}
            {!isEmpty(items) && <SearchResults items={items} />}
        </>
    );
};

SearchFilter.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};

export default SearchFilter;
